import { DefaultError, MutationObserverOptions, useMutation, useQuery, UseQueryOptions } from '@tanstack/vue-query';
import { MaybeRefOrGetter, toValue } from 'vue';

import {
  ExpressMenusApiExpressMenusCreateRequest,
  ExpressMenusApiExpressMenusPartialUpdateRequest,
  ProductApiExpressMenusListRequest,
  UnifiedExpressProductMenu,
} from '@/clients/unifiedApi';
import {
  createExpressMenu,
  getExpressMenusList,
  partialUpdateExpressMenu,
  updateExpressMenu,
} from '@/services/api/express-menus';

/**
 * query key can take an object of additional options
 * @see https://tanstack.com/query/v4/docs/framework/vue/guides/query-keys#array-keys-with-variables
 */
export const expressMenusQueryKey = {
  expressMenuList: ({ filters }: ExpressMenuListQueryKeyArgs) => ['expressMenus', filters] as const,
};

interface ExpressMenuListQueryKeyArgs {
  filters: MaybeRefOrGetter<ProductApiExpressMenusListRequest>;
}

type UseExpressMenuListQueryArgs = Partial<UseQueryOptions<UnifiedExpressProductMenu[]>> & ExpressMenuListQueryKeyArgs;

export const useExpressMenuListQuery = ({ filters, ...options }: UseExpressMenuListQueryArgs) => {
  return useQuery({
    queryKey: expressMenusQueryKey.expressMenuList({ filters }),
    queryFn: () => getExpressMenusList(toValue(filters)),
    ...options,
  });
};

type UseCreateExpressMenuMutationArgs = MutationObserverOptions<
  UnifiedExpressProductMenu,
  DefaultError,
  ExpressMenusApiExpressMenusCreateRequest
>;

export const useCreateExpressMenuMutation = (options?: UseCreateExpressMenuMutationArgs) => {
  return useMutation({
    mutationFn: createExpressMenu,
    ...options,
  });
};

type UseUpdateExpressMenuMutationArgs = MutationObserverOptions<
  UnifiedExpressProductMenu,
  DefaultError,
  ExpressMenusApiExpressMenusCreateRequest
>;

export const useUpdateExpressMenuMutation = (options?: UseUpdateExpressMenuMutationArgs) => {
  return useMutation({
    mutationFn: updateExpressMenu,
    ...options,
  });
};

type UsePartialUpdateExpressMenuMutationArgs = MutationObserverOptions<
  UnifiedExpressProductMenu,
  DefaultError,
  ExpressMenusApiExpressMenusPartialUpdateRequest
>;

export const usePartialUpdateExpressMenuMutation = (options?: UsePartialUpdateExpressMenuMutationArgs) => {
  return useMutation({
    mutationFn: partialUpdateExpressMenu,
    ...options,
  });
};
